import { DEFAULT_PAGE_INFO, ListingStatus, SortInput } from '@apeiron/common'
import {
  PlanetFilterInput,
  PlanetMiddleLite,
  PlanetSortBy,
  PlanetStage
} from '@apeiron/planet'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { planetMiddleLiteSchema } from '@src/deserialize/yup/planet'
import graphql from '@src/graphql/query/getMyPlanetsMiddleLite'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetMyPlanetsMiddleLite = (): Hook => {
  const [fetchPlanets] = useLazyQuery(graphql)

  const [data, setData] = useState<PlanetMiddleLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const bookmarked = R.propOr(false, 'bookmarked', request) as boolean

      const nftListingStatus = R.propOr(
        undefined,
        'nftListingStatus',
        request
      ) as ListingStatus | undefined

      const planetIds = R.propOr([], 'planetIds', request) as string[]

      const stage = R.propOr(null, 'stage', request)

      const pageInput = R.propOr({}, 'pageInput', request) as PageInput

      const filterInput = R.propOr(
        {},
        'filterInput',
        request
      ) as PlanetFilterInput

      const sortInput = R.propOr(
        {
          sortBy: PlanetSortBy.PriceAsc
        },
        'sortInput',
        request
      ) as SortInput<PlanetSortBy>

      const variables = {
        isBookmarked: bookmarked,
        filterInput: {
          ...filterInput,
          ...(!R.isEmpty(planetIds) ? { planetID: planetIds } : {}),
          ...(!R.isNil(stage) ? { stage } : {}),
          ...(!R.isNil(nftListingStatus) ? { nftListingStatus } : {})
        },
        pagingInput: serializePagingInput(pageInput),
        sortInput
      }

      const response = await fetchPlanets({ variables })

      const planets = deserializeListData<PlanetMiddleLite>(
        response,
        planetMiddleLiteSchema,
        {}
      )

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(planets)
      setPageInfo(pageInfo)

      return {
        data: planets,
        pageInfo
      }
    },
    [fetchPlanets]
  )

  return {
    data,
    pageInfo,
    fetch
  }
}

type Request = {
  bookmarked?: boolean
  nftListingStatus?: ListingStatus
  planetIds?: [string]
  stage?: PlanetStage[]
  pageInput?: PageInput
  filterInput?: PlanetFilterInput
  sortInput?: SortInput<PlanetSortBy>
}

type Response = {
  data: PlanetMiddleLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: PlanetMiddleLite[]
  pageInfo: PageInfo
  fetch: (request: Request) => Promise<Response>
}

export default useGetMyPlanetsMiddleLite
